import { useRef, useState } from 'react'
import { AlertTriangle, FileSpreadsheet, Upload, X } from 'lucide-react'

import {
  brokerDirectoryService,
  type BrokerImportPreview,
  type BrokerImportRow,
} from '@/services/brokerDirectory'

const HEADER_ALIASES: Record<string, keyof BrokerImportRow> = {
  firm: 'firm',
  brokerage: 'firm',
  'brokerage firm': 'firm',
  company: 'firm',
  contact: 'contactName',
  'contact name': 'contactName',
  broker: 'contactName',
  name: 'contactName',
  email: 'contactEmail',
  'contact email': 'contactEmail',
  phone: 'contactPhone',
  'contact phone': 'contactPhone',
  title: 'contactTitle',
  'contact title': 'contactTitle',
}

function csvCells(line: string, delimiter: string) {
  const cells: string[] = []
  let cell = ''
  let quoted = false
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    if (char === '"' && quoted && line[index + 1] === '"') {
      cell += '"'
      index += 1
    } else if (char === '"') {
      quoted = !quoted
    } else if (char === delimiter && !quoted) {
      cells.push(cell.trim())
      cell = ''
    } else {
      cell += char
    }
  }
  cells.push(cell.trim())
  return cells
}

export function parseBrokerRoster(text: string): BrokerImportRow[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim())
  if (lines.length < 2) return []
  const delimiter = lines[0].includes('\t') ? '\t' : ','
  const headers = csvCells(lines[0], delimiter).map(
    (header) => HEADER_ALIASES[header.trim().toLowerCase()],
  )
  if (!headers.includes('firm')) return []
  return lines.slice(1).flatMap((line) => {
    const values = csvCells(line, delimiter)
    const row: BrokerImportRow = { firm: '' }
    headers.forEach((header, index) => {
      if (header && values[index]) row[header] = values[index]
    })
    return row.firm.trim() ? [{ ...row, firm: row.firm.trim() }] : []
  })
}

async function rowsFromFile(file: File) {
  if (/\.xlsx$/i.test(file.name)) {
    // ExcelJS is intentionally loaded only when an Excel file is chosen; it is
    // too large for the normal CRM route bundle.
    const ExcelJS = await import('exceljs')
    const workbook = new ExcelJS.Workbook()
    await workbook.xlsx.load(await file.arrayBuffer())
    const sheet = workbook.worksheets[0]
    if (!sheet) return []
    const lines: string[] = []
    sheet.eachRow((row) => {
      const values = row.values as unknown[]
      lines.push(values.slice(1).map((value) => String(value ?? '')).join('\t'))
    })
    return parseBrokerRoster(lines.join('\n'))
  }
  return parseBrokerRoster(await file.text())
}

export default function BrokerBulkImportDialog({
  open,
  onClose,
  onCommitted,
}: {
  open: boolean
  onClose: () => void
  onCommitted: () => void | Promise<void>
}) {
  const [text, setText] = useState('')
  const [rows, setRows] = useState<BrokerImportRow[]>([])
  const [preview, setPreview] = useState<BrokerImportPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  if (!open) return null

  function updateText(value: string) {
    setText(value)
    setRows(parseBrokerRoster(value))
    setPreview(null)
    setError(null)
  }

  async function chooseFile(file: File | undefined) {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const parsed = await rowsFromFile(file)
      if (parsed.length === 0) throw new Error('no rows')
      setRows(parsed)
      setText('')
      setPreview(null)
    } catch {
      setError('No importable rows were found. Include a Firm or Brokerage column.')
    } finally {
      setBusy(false)
    }
  }

  async function runPreview() {
    if (rows.length === 0) return
    setBusy(true)
    setError(null)
    try {
      setPreview(await brokerDirectoryService.previewImport(rows))
    } catch {
      setError('The preview could not be generated. Nothing was written.')
    } finally {
      setBusy(false)
    }
  }

  async function commit() {
    if (!preview?.previewToken) return
    setBusy(true)
    setError(null)
    try {
      await brokerDirectoryService.commitImport(rows, preview.previewToken)
      await onCommitted()
      onClose()
    } catch {
      setPreview(null)
      setError('The rows changed or the commit failed. Preview again; nothing was written.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-label="Import broker directory">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl border border-amber-400/20 bg-stone-950 shadow-2xl">
        <div className="flex items-start justify-between border-b border-amber-400/10 p-4">
          <div>
            <h2 className="text-sm font-medium text-stone-100">Import broker directory</h2>
            <p className="mt-1 text-[11px] text-stone-500">Paste Excel rows or choose an .xlsx/.csv file. Preview is mandatory; existing fields are never overwritten.</p>
          </div>
          <button type="button" aria-label="Close import" className="text-stone-500 hover:text-stone-200" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="space-y-4 p-4">
          <div className="rounded border border-amber-400/15 bg-amber-400/[0.04] p-3 text-[11px] text-stone-300">
            <strong className="text-amber-200">Client list not loaded.</strong> This tool remains empty and fail-closed until Antarctica supplies the actual file. No sample brokers are embedded.
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-secondary flex items-center gap-1.5 text-[11px]" onClick={() => fileRef.current?.click()} disabled={busy}>
              <Upload size={12} /> Choose Excel or CSV
            </button>
            <input ref={fileRef} className="hidden" type="file" accept=".xlsx,.csv,.tsv,.txt" onChange={(event) => void chooseFile(event.target.files?.[0])} />
            <span className="self-center text-[10px] text-stone-600">Required header: Firm or Brokerage. Optional: Contact Name, Email, Phone, Title.</span>
          </div>
          <textarea
            aria-label="Pasted broker roster"
            className="input min-h-36 w-full font-mono text-[11px]"
            value={text}
            onChange={(event) => updateText(event.target.value)}
            placeholder={'Firm\tContact Name\tEmail\tPhone\tTitle\nExample Brokerage\tJane Broker\tjane@example.com'}
          />
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-stone-500">{rows.length} importable row{rows.length === 1 ? '' : 's'}</span>
            <button type="button" className="btn-primary flex items-center gap-1.5 text-[11px]" disabled={busy || rows.length === 0} onClick={() => void runPreview()}>
              <FileSpreadsheet size={12} /> Preview changes
            </button>
          </div>
          {preview && (
            <div className="space-y-2" data-testid="broker-import-preview">
              <div className="grid grid-cols-3 gap-2 text-center text-[11px]">
                <div className="rounded border border-emerald-400/15 bg-emerald-400/[0.04] p-2"><strong className="block text-emerald-300">{preview.firmsToCreate}</strong> firms to create</div>
                <div className="rounded border border-emerald-400/15 bg-emerald-400/[0.04] p-2"><strong className="block text-emerald-300">{preview.contactsToCreate}</strong> contacts to create</div>
                <div className="rounded border border-orange-400/15 bg-orange-400/[0.04] p-2"><strong className="block text-orange-300">{preview.heldForReview}</strong> held, not written</div>
              </div>
              <div className="max-h-52 overflow-auto rounded border border-amber-400/10">
                {preview.rows.map((row) => (
                  <div key={row.row} className="grid grid-cols-[1fr_1fr_auto] gap-2 border-b border-amber-400/5 px-2 py-1.5 text-[10px] last:border-0">
                    <span className="text-stone-200">{row.firm || row.reason}</span>
                    <span className="text-stone-400">{row.contact || '—'}</span>
                    <span className={row.firmAction === 'held-near-duplicate' || row.contactAction === 'held-near-duplicate' ? 'text-orange-300' : 'text-emerald-300'}>
                      {row.firmAction ?? row.status} / {row.contactAction ?? 'no contact'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex items-start gap-2 text-[10px] text-stone-500"><AlertTriangle size={12} className="mt-0.5 text-orange-400" /> Similar rows are held for manual review. Committing creates only exact-safe new records.</div>
            </div>
          )}
          {error && <p className="text-[11px] text-red-300">{error}</p>}
          <div className="flex justify-end gap-2 border-t border-amber-400/10 pt-3">
            <button type="button" className="btn-secondary text-[11px]" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary text-[11px]" disabled={busy || !preview?.previewToken} onClick={() => void commit()}>
              {busy ? 'Working…' : 'Commit previewed rows'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
